import React, { useEffect, useState } from 'react';
import api from './api';
import Kanban from './Kanban';
import Gantt from './Gantt';
import ActivityLog from './ActivityLog';
import ProgressChart from './ProgressChart';
import CalendarView from './CalendarView';
import TaskListView from './TaskListView';
import TaskModal from './TaskModal';
import ProjectModal from './ProjectModal';
import CancelProjectModal from './CancelProjectModal';
import Deliverables from './Deliverables';
import ProjectReport from './ProjectReport';
import { projectCode, PROJECT_STATUS_LABELS, CAN_CANCEL_PROJECT_ROLES, CAN_ARCHIVE_PROJECT_ROLES, formatDate } from './helpers';

const TABS = [
  { key: 'kanban', label: 'Kanban' },
  { key: 'gantt', label: 'Gantt' },
  { key: 'calendar', label: 'Calendrier' },
  { key: 'list', label: 'Liste des tâches' },
  { key: 'deliverables', label: 'Livrables' },
  { key: 'report', label: 'Rapport' },
  { key: 'activity', label: 'Activité' },
];

function ProjectDetail({ project, users, currentUser, onBack, onProjectChanged }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('kanban');
  const [openTask, setOpenTask] = useState(undefined); // undefined = closed, null = new task
  const [newTaskStatus, setNewTaskStatus] = useState('Todo');
  const [showEditProject, setShowEditProject] = useState(false);
  const [showCancelProject, setShowCancelProject] = useState(false);
  const [proj, setProj] = useState(project);
  const [commentCounts, setCommentCounts] = useState({});
  const [attachmentCounts, setAttachmentCounts] = useState({});
  const [deliverableCount, setDeliverableCount] = useState(0);
  const [archivage, setArchivage] = useState('');
  const [historique, setHistorique] = useState([]);

  // Archiver ne supprime rien : le projet sort simplement des vues courantes
  // (tableau de bord, calendrier, achats, alertes) et reste consultable dans
  // l'onglet « Archivés ». On peut le restaurer à tout moment.
  const basculerArchive = (archiver) => {
    setArchivage('');
    api
      .put(`/projects/${proj.id}/archive`, { archived: archiver })
      .then((res) => {
        setProj(res.data);
        if (onProjectChanged) onProjectChanged(res.data);
      })
      .catch((err) =>
        setArchivage(err.response?.data?.message || "L'opération a échoué.")
      );
  };

  const loadTasks = () => {
    setLoading(true);
    api
      .get('/tasks', { params: { projectId: proj.id } })
      .then((res) => setTasks(res.data))
      .catch(() => setError('Impossible de charger les tâches du projet.'))
      .finally(() => setLoading(false));
  };

  const loadCounts = () => {
    Promise.all([
      api.get('/comments', { params: { projectId: proj.id } }),
      api.get('/attachments', { params: { projectId: proj.id } }),
    ])
      .then(([commentsRes, attachmentsRes]) => {
        const cCounts = {};
        commentsRes.data.forEach((c) => {
          if (c.task_id) cCounts[c.task_id] = (cCounts[c.task_id] || 0) + 1;
        });
        const aCounts = {};
        attachmentsRes.data.forEach((a) => {
          if (a.task_id) aCounts[a.task_id] = (aCounts[a.task_id] || 0) + 1;
        });
        setCommentCounts(cCounts);
        setAttachmentCounts(aCounts);
        setDeliverableCount(attachmentsRes.data.length);
      })
      .catch(() => {});
  };

  // Courbe d'avancement du projet : un relevé par jour, côté serveur.
  useEffect(() => {
    api
      .get('/progress-history', { params: { projectId: proj.id } })
      .then((res) => setHistorique(res.data))
      .catch(() => setHistorique([]));
  }, [proj.id, tasks.length]);

  useEffect(loadTasks, [proj.id]);
  useEffect(loadCounts, [proj.id, activeTab]);

  const openCount = tasks.filter((t) => t.status !== 'Done').length;

  const handleOpenTask = (task, statusForNew = 'Todo') => {
    setNewTaskStatus(statusForNew);
    setOpenTask(task || null);
  };

  const handleTaskSaved = () => {
    setOpenTask(undefined);
    loadTasks();
    loadCounts();
  };

  const handleTaskDeleted = () => {
    setOpenTask(undefined);
    loadTasks();
    loadCounts();
  };

  return (
    <div className="fade-in">
      <button className="btn" onClick={onBack} style={{ marginBottom: 16 }}>
        ← Tous les projets
      </button>

      <div className="page-header-row" style={{ alignItems: 'flex-start' }}>
        <div>
          <div className="project-ref">{projectCode(proj.id)} · {PROJECT_STATUS_LABELS[proj.status] || proj.status}</div>
          <h1 style={{ margin: '4px 0 0' }}>{proj.name}</h1>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {CAN_ARCHIVE_PROJECT_ROLES.includes(currentUser?.role) && (
            <button className="btn" onClick={() => basculerArchive(!proj.archived)}>
              {proj.archived ? "Sortir de l'archive" : 'Archiver le projet'}
            </button>
          )}
          {CAN_CANCEL_PROJECT_ROLES.includes(currentUser?.role) && proj.status !== 'Cancelled' && !proj.archived && (
            <button className="btn btn-danger" onClick={() => setShowCancelProject(true)}>Annuler le projet</button>
          )}
          <button className="btn" onClick={() => setShowEditProject(true)}>Modifier le projet</button>
        </div>
      </div>

      {archivage && <div className="error-message" style={{ marginBottom: 16 }}>{archivage}</div>}

      {proj.archived && (
        <div className="error-message" style={{ marginBottom: 16 }}>
          <strong>Projet archivé{proj.archived_at ? ` le ${formatDate(proj.archived_at)}` : ''}
          {proj.archived_by ? ` par ${proj.archived_by}` : ''}.</strong> Il n'apparaît plus dans
          le tableau de bord, le calendrier ni le suivi des achats. Les données sont conservées.
        </div>
      )}

      {proj.status === 'Cancelled' && proj.cancel_reason && (
        <div className="error-message" style={{ marginBottom: 16 }}>
          <strong>Projet annulé.</strong> Raison : {proj.cancel_reason}
        </div>
      )}

      <div className="detail-stats">
        <div>
          <div className="detail-stat-label">Avancement</div>
          <div className="detail-stat-value" style={{ color: 'var(--accent)' }}>{proj.progress_percent}%</div>
        </div>
        <div>
          <div className="detail-stat-label">Budget</div>
          <div className="detail-stat-value">{(proj.budget_allocated / 1000000).toFixed(1)} M MAD</div>
        </div>
        <div>
          <div className="detail-stat-label">Tâches ouvertes</div>
          <div className="detail-stat-value">{openCount}</div>
        </div>
      </div>

      <div className="tab-bar">
        {TABS.map((t) => (
          <div
            key={t.key}
            className={`tab-item ${activeTab === t.key ? 'active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </div>
        ))}
      </div>

      {loading && <p>Chargement des tâches...</p>}
      {error && <div className="error-message">{error}</div>}

      {!loading && !error && (
        <>
          {activeTab === 'kanban' && (
            <Kanban tasks={tasks} users={users} onOpenTask={handleOpenTask} onTaskUpdated={loadTasks} />
          )}
          {activeTab === 'gantt' && <Gantt tasks={tasks} users={users} onOpenTask={handleOpenTask} />}
          {activeTab === 'calendar' && <CalendarView tasks={tasks} onOpenTask={handleOpenTask} />}
          {activeTab === 'list' && (
            <TaskListView
              tasks={tasks}
              users={users}
              onOpenTask={handleOpenTask}
              commentCounts={commentCounts}
              attachmentCounts={attachmentCounts}
            />
          )}
          {activeTab === 'deliverables' && (
            <Deliverables projectId={proj.id} users={users} currentUser={currentUser} tasks={tasks} />
          )}
          {activeTab === 'activity' && (
            <>
              <ProgressChart points={historique} titre="Avancement du projet dans le temps" />
              <ActivityLog projectId={proj.id} users={users} />
            </>
          )}
          {activeTab === 'report' && (
            <ProjectReport project={proj} tasks={tasks} users={users} deliverableCount={deliverableCount} />
          )}
        </>
      )}

      {openTask !== undefined && (
        <TaskModal
          task={openTask}
          projectId={proj.id}
          users={users}
          currentUser={currentUser}
          initialStatus={newTaskStatus}
          onClose={() => setOpenTask(undefined)}
          onSaved={handleTaskSaved}
          onDeleted={handleTaskDeleted}
        />
      )}

      {showEditProject && (
        <ProjectModal
          project={proj}
          users={users}
          onClose={() => setShowEditProject(false)}
          onSaved={(updated) => { setProj(updated); setShowEditProject(false); if (onProjectChanged) onProjectChanged(updated); }}
          onDeleted={() => { setShowEditProject(false); onBack(); if (onProjectChanged) onProjectChanged(null); }}
        />
      )}

      {showCancelProject && (
        <CancelProjectModal
          project={proj}
          currentUser={currentUser}
          onClose={() => setShowCancelProject(false)}
          onCancelled={(updated) => { setProj(updated); setShowCancelProject(false); if (onProjectChanged) onProjectChanged(updated); }}
        />
      )}
    </div>
  );
}

export default ProjectDetail;
